import { useQuery } from "@tanstack/react-query"
import { useAxios } from "../../contexts/AxiosContext"
import { ExperimentStatus } from "@prismancy/shared"
import { Experiment } from "../../types"
import { queryKeys } from "../../queryKeys"

type UseExperimentType = {
  experiment: Experiment | undefined
  isLoading: boolean,
  isExperimentDisabled: boolean,
  error: Error | null
}

type UseExperimentProps = {
  experimentId: string | undefined
}

type ExperimentResponse = {
  status: number
  experiment: Experiment
}

const useExperiment = ({ experimentId = '' }: UseExperimentProps): UseExperimentType => {

  const { axiosApiInstance } = useAxios()

  const { data: experiment, isLoading, error } = useQuery({
    queryKey: queryKeys.experiments.detail(experimentId),
    queryFn: async (): Promise<Experiment> => {
      const response = await axiosApiInstance.get<ExperimentResponse>(`/experiment/experiment/${experimentId}`)
      return response.data.experiment
    },
    enabled: Boolean(experimentId),
    select: (data) => {
      // transform the experiment data to the desired format
      // objective id is converted to a number and traffic allocation is converted to a number
      return {
        ...data,
        objective_id: Number(data.objective_id || 0),
        traffic_allocation: Number(data.traffic_allocation || 0),
        published_source_traffic_allocation:
          data.published_source_traffic_allocation == null
            ? null
            : Number(data.published_source_traffic_allocation),
        published_source_variant_weight:
          data.published_source_variant_weight == null
            ? null
            : Number(data.published_source_variant_weight),
      }
    },
  })

  const isExperimentDisabled = experiment?.status === ExperimentStatus.LIVE || experiment?.status === ExperimentStatus.ENDED

  return { experiment, isLoading, error, isExperimentDisabled }
}

export default useExperiment
